<template>
    <div class="relationships-hub-toolbar border-b border-ultralight bg-surface-raised">
        <div
            v-if="showViewSwitch"
            aria-label="Ansicht auswählen"
            class="relationships-hub-view-switch"
        >
            <button
                :aria-pressed="viewMode === 'list' ? 'true' : 'false'"
                :class="{'relationships-hub-view-active': viewMode === 'list'}"
                aria-label="Listenansicht"
                class="relationships-hub-view-button"
                title="Listenansicht"
                type="button"
                @click="$emit('update:viewMode', 'list')"
            >
                <svg-vue class="h-4 w-4" icon="font-awesome.regular.bars"></svg-vue>
            </button>
            <button
                :aria-pressed="viewMode === 'grid' ? 'true' : 'false'"
                :class="{'relationships-hub-view-active': viewMode === 'grid'}"
                aria-label="Kachelansicht"
                class="relationships-hub-view-button"
                title="Kachelansicht"
                type="button"
                @click="$emit('update:viewMode', 'grid')"
            >
                <svg-vue class="h-4 w-4" icon="font-awesome.regular.grid-2"></svg-vue>
            </button>
        </div>

        <div
            v-on-clickaway="closeSectionMenu"
            class="relationships-hub-section-select relative flex min-w-0 flex-shrink-0 items-center border-r border-ultralight"
        >
            <button
                :aria-expanded="sectionMenuOpen ? 'true' : 'false'"
                aria-haspopup="listbox"
                class="relationships-hub-section-trigger relative flex h-full min-w-[18rem] max-w-[26rem] items-center bg-surface-raised py-0 pl-5 pr-10 text-left text-sm font-medium leading-5 text-gray-800 focus:outline-none"
                type="button"
                @click="sectionMenuOpen = !sectionMenuOpen"
            >
                <svg-vue :icon="currentSection.icon" class="mr-3 h-5 w-5 flex-shrink-0 text-gray-400"></svg-vue>
                <span class="min-w-0 flex-1 truncate">{{ currentSection.label }}</span>
                <svg-vue
                    class="absolute right-4 h-[0.875rem] w-[0.875rem] text-gray-900"
                    icon="font-awesome.regular.angle-down"
                ></svg-vue>
            </button>

            <div
                v-show="sectionMenuOpen"
                class="relationships-hub-section-menu absolute left-0 top-full mt-2 min-w-full overflow-hidden rounded border border-bordergray-100 bg-white shadow-lg"
                role="listbox"
            >
                <button
                    v-for="section in sectionOptions"
                    :key="section.value"
                    :aria-selected="activeSection === section.value ? 'true' : 'false'"
                    :class="activeSection === section.value ? 'bg-gray-50 font-semibold text-gray-900' : 'font-normal text-gray-700 hover:bg-gray-50'"
                    class="flex w-full min-w-0 items-center px-4 py-2 text-left text-sm"
                    role="option"
                    type="button"
                    @click="selectSection(section)"
                >
                    <svg-vue :icon="section.icon" class="mr-3 h-5 w-5 flex-shrink-0 text-gray-400"></svg-vue>
                    <span class="min-w-0 flex-1 truncate">{{ section.label }}</span>
                </button>
            </div>
        </div>

        <div v-if="showActions && $slots.actions" class="relationships-hub-actions">
            <slot name="actions"></slot>
        </div>
    </div>
</template>

<script>
export default {
    name: 'relationships-hub-toolbar',
    emits: ['update:viewMode'],
    props: {
        activeSection: {
            type: String,
            required: true,
        },
        viewMode: {
            type: String,
            required: true,
        },
        showViewSwitch: {
            type: Boolean,
            default: true,
        },
        showActions: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            sectionMenuOpen: false,
        };
    },
    computed: {
        sectionOptions() {
            return [
                {
                    value: 'contacts',
                    label: 'Personen',
                    icon: 'font-awesome.regular.user',
                    path: this.contactsPath,
                },
                {
                    value: 'companies',
                    label: 'Unternehmen',
                    icon: 'font-awesome.regular.buildings',
                    path: this.companiesPath,
                },
            ];
        },
        currentSection() {
            return this.sectionOptions.find((section) => section.value === this.activeSection)
                || this.sectionOptions[0];
        },
        isWorkspaceRoute() {
            return String(this.$route && this.$route.path ? this.$route.path : '').startsWith('/workspace');
        },
        contactsPath() {
            return this.isWorkspaceRoute ? '/workspace/relationships' : '/relationships';
        },
        companiesPath() {
            return this.isWorkspaceRoute ? '/workspace/relationships/companies' : '/relationships/companies';
        },
    },
    methods: {
        closeSectionMenu() {
            this.sectionMenuOpen = false;
        },
        selectSection(section) {
            this.closeSectionMenu();
            if (!section || section.value === this.activeSection) {
                return;
            }
            this.$router.push(section.path);
        },
    },
};
</script>

<style scoped>
.relationships-hub-toolbar {
    display: flex;
    min-height: 3.25rem;
    align-items: stretch;
}

.relationships-hub-view-switch {
    display: inline-flex;
    align-items: center;
}

.relationships-hub-section-select {
    min-width: 18rem;
}

.relationships-hub-section-trigger {
    width: 100%;
}

.relationships-hub-section-menu {
    z-index: 120;
}

.relationships-hub-view-switch {
    gap: 0.25rem;
    padding: 0 1rem;
    border-right: 1px solid var(--border-ultralight);
}

.relationships-hub-actions {
    display: flex;
    min-width: 3.25rem;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    padding: 0 0.625rem;
}

.relationships-hub-view-button {
    display: inline-flex;
    width: 1.75rem;
    height: 1.75rem;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 0.25rem;
    background: transparent;
    color: var(--ui-text-secondary);
}

.relationships-hub-view-button:focus,
.relationships-hub-view-button:focus-visible,
.relationships-hub-view-button:active {
    outline: none;
    box-shadow: none;
}

.relationships-hub-view-active {
    color: var(--ui-text-subtle);
    cursor: default;
}
</style>
